import { call, put, select, takeEvery, takeLatest, all } from 'redux-saga/effects'
import {delay} from 'redux-saga'
import { push } from 'react-router-redux'
import { cloneDeep } from 'lodash'
import {
  generatePdfReport,
  getDecisionResult,
  recallSession,
  sendReportEmail,
  saveSession,
  getSessionTranslation,
  addSessionTranslation,
  setSessionTranslation
} from '../api'
import {serializeSession, transmogrifyApiDataToSessionStore as deserializeSession} from '../api/formatters'
import * as actions from '../actionTypes'
import {DCP_SESSION_ALIVE_EXPIRATION} from '../constants'
import {getPathnameEnding, setCookie, timeoutPromise, difference} from '../utils'
import {checkFactorEditing} from './helpers'
import {isSessionDisabled} from '../selectors'

/** Clone and return unmodified session obj with removed read only keys  */
const removeReadOnlyKeys = session => {
  const temp = cloneDeep(session)
  delete temp.options
  delete temp.optionsResults
  delete temp.educationMaterials
  delete temp.decisionName
  return temp
}

export const isSaveRequired = (lastScreen, session) => {
  const original = removeReadOnlyKeys(session.unmodifiedApiResponse)
  const updated = JSON.parse(serializeSession(lastScreen, session))
  let isRequired = false
  if (!session.unmodifiedApiResponse.isComplete) { // if complete we never save
    isRequired = difference(original, updated).length !== 0
  }
  return isRequired
}

export function* counselingSessionNavigationSaga(action) {
  try {
    const {lastScreenSaved, pathname, sessionId, shouldGotoNextPage} = action.payload
    const counselingSession = yield select(state => state.counselingSession)
    const counselingSessionClone = cloneDeep(counselingSession)
    if(isSaveRequired(lastScreenSaved, counselingSessionClone)) {
      counselingSessionClone.counselingSessionData.shouldGotoNextPage = shouldGotoNextPage
      const serializedSession = serializeSession(lastScreenSaved, counselingSessionClone)
      const isSaved = yield call(saveCounselingSessionSaga, serializedSession, counselingSession.sessionId)
      if(isSaved && pathname === '/session/report') {
        yield put({type: actions.GET_DECISION_RESULT, payload: {sessionId}})
      } else if(!isSaved) {
        throw new Error()
      }
    }
    if (action.payload.pathname) {
      yield put(push(pathname))
    } else { // Save & Logout
      yield put({ type: actions.LOGOUT_REQUESTED })
    }
  } catch(error) {
    yield put({type: actions.COUNSELING_SESSION_NAVIGATION_HALTED, payload: {error}})
  }
}

export function* generatePdfReportSaga(action) {
  const {lang, sessionId} = action.payload
  try {
    const {pdfURL} = yield generatePdfReport(sessionId, lang)
    yield put({type: actions.SESSION_REPORT_PDF_GENERATION_SUCCESS, payload: {pdfURL}})
  } catch(error) {
    yield put({type: actions.SESSION_REPORT_PDF_GENERATION_FAILURE, payload: {error}})
  }
}

export function* getDecisionResultSaga(action) {
  try {
    const response = yield getDecisionResult(action.payload.sessionId)
    yield put({type: actions.GET_DECISION_RESULT_SUCCESS, payload: {response}})
  } catch(e) {
    // TODO: handle error
    // yield put({type: actions.GET_DECISION_RESULT_FAILURE})
  }
}

export function* recallCounselingSessionSaga(action) {
  try {
    const {ignoreLastSavedPage, sessionId, path} = action.payload
    const session = yield recallSession(sessionId)
    session.primaryLanguageCode = session.primaryLanguageCode.toLowerCase()
    const appLang = yield select(state => state.globalSettings.lang)
    const sessionLangCode = session.primaryLanguageCode.toLowerCase()

    yield put({
      type: actions.SESSION_RECALL_SUCCESS,
      payload: {
        apiDataReadyForStore: deserializeSession(session),
        sessionId,
        unmodifiedApiResponse: session
      }
    })
    setCookie('DcpSessionId', sessionId, DCP_SESSION_ALIVE_EXPIRATION)

    // Session lang doesn't match current app lang
    if (sessionLangCode !== appLang) {
      yield put({
        type: actions.SESSION_LANGUAGE_REQUESTED,
        payload: {
          lang : sessionLangCode
        }
      })
    }

    const {counselingSessionData, pages} = yield select(state => state.counselingSession)
    if(!ignoreLastSavedPage) {
      let destinationPathname
      if (path) { // Custom path
        destinationPathname = path
      } else { // Last Page/Report
        if(counselingSessionData.isComplete) { // completed session always recalls to report page
          destinationPathname = '/session/report'
        } else {
          const pageIndex = pages.reduce((acc, page, i) => {
            if (getPathnameEnding(page.pathname) === session.lastScreenSaved) {
              acc = session.shouldGotoNextPage ? i + 1 : i
            }
            return acc
          }, -1)
          const page = pageIndex >= 0 ? pages[pageIndex] : null
          // if no match, go to first counseling session page
          destinationPathname = page ? page.pathname : pages[0].pathname
        }
      }
      yield put(push(destinationPathname))
    }
  } catch(error) {
    yield put({type: actions.SESSION_RECALL_FAILURE, payload: {error}})
  }
}

export function* sendReportEmailSaga(action) {
  const { recipients, content, sessionId, lang } = action.payload
  try {
    yield sendReportEmail(recipients, content, sessionId, lang)
    yield put({type: actions.SEND_EMAIL_SUCCESS})
  } catch(error) {
    yield put({type: actions.SEND_EMAIL_FAIL})
  }
}

/**
 * Save a counseling session + dispatch events
 * @param {string} serializedSession
 * @param {number} sessionId
 * @param {Iterator|Promise} delay - An iterator or promise used to delay `COUNSELING_SESSION_SAVE_SUCCESS` event
 */
export function* saveCounselingSessionSaga(serializedSession, sessionId, delay) {
  try {
    const isCounselingSessionDisabled = yield select(isSessionDisabled)
    // Only save the session is incomplete or the user is an admin
    if (!isCounselingSessionDisabled) {
      yield put({type: actions.COUNSELING_SESSION_SAVE_REQUESTED})
      const isSaved = yield saveSession(serializedSession, sessionId)

      if(delay)
        yield delay

      yield put({type: actions.COUNSELING_SESSION_SAVE_SUCCESS})

      return isSaved
    } else {
      return true
    }
  } catch(error) {
    yield put({type: actions.COUNSELING_SESSION_SAVE_FAILURE, payload: {error}})
  }
}

export function* saveCounselingSessionOutsideOfNavSaga(lastPageSaved) {
  try {
    const counselingSession = yield select(state => state.counselingSession)

    const serializedSession = serializeSession(lastPageSaved, counselingSession)

    const isSaved = yield saveCounselingSessionSaga(serializedSession, counselingSession.sessionId)

    return isSaved
  } catch(error) {
    yield put({type: actions.COUNSELING_SESSION_SAVE_FAILURE, payload: {error}})
  }
}

export function* saveSessionSendEmailSaga(action) {
  yield saveCounselingSessionOutsideOfNavSaga(action.payload.lastPageSaved)

  yield sendReportEmailSaga(action)
}

export function* updateUserCommentsSaga({payload}) {
  try {
    yield put({type: actions.UPDATE_USER_COMMENTS, payload})

    const counselingSession = yield select(state => state.counselingSession)

    const serializedSession = serializeSession(payload.lastScreen || '', counselingSession)

    const isSaved = yield saveCounselingSessionSaga(serializedSession, counselingSession.sessionId, timeoutPromise())

    if(isSaved) {
      yield put({type: actions.UPDATE_USER_COMMENTS_SUCCESS})
    } else {
      throw new Error('Save failed')
    }
  } catch(error) {
    yield put({type: actions.UPDATE_USER_COMMENTS_FAILURE, payload: {error: error.message}})
  }
}

export function* sessionExit() {
  try {
    const isEditingFactor = yield checkFactorEditing(actions.SESSION_EXIT_REQUESTED)

    if(isEditingFactor)
      return

    yield put({type: actions.SESSION_EXIT})
    yield put({ type: actions.SESSION_LANGUAGE_REQUESTED, payload: { lang: null } })
    yield put(push('/')) // redirect
  } catch (e) {}
}

export function* dismissFactorActionModal({payload}) {
  try {
    const type = yield select(state => state.counselingSession.factorActionRequester)

    yield put({type: actions.DISMISS_FACTOR_ACTION_MODAL})

    if (payload.dismiss) {
      return
    } else if (payload.save) {
      yield saveCounselingSessionOutsideOfNavSaga(payload.lastPageSaved)
    }

    yield put({type})
  } catch(e) {
    console.log(e)
  }
}

/**
 * Get session translations if there is only the original
 * add english and add it to the object
 * @param {object} payload
 */
export function* getSessionTranslationsSaga({ payload }) {
  try {
    yield put({ type: actions.SESSION_TRANSLATION_INIT })
    const sessionLangCode = payload.sessionLangCode
    const translations = yield all(payload.translations.reduce((acc, ele)  => {
      const langCode = ele.languageCode.toLowerCase()
      acc[langCode] = call(() => getSessionTranslation(payload.sessionId, langCode))
      return acc;
    }, {}))

    const original = translations[sessionLangCode]
    let others = {}
    for (let key in translations) {
      if (key !== sessionLangCode) {
        others[key] = translations[key]
      }
    }
    yield put({ type: actions.SESSION_TRANSLATION_SUCCESS, payload: { translations, original, others } })
  } catch(error) {
    yield put({ type: actions.SESSION_TRANSLATION_FAIL, payload: error })
  }
}



export function* setSessionTranslationsSaga({ payload }) {
  try {
    const sessionTranslations = yield select(state => state.counselingSession.counselingSessionData.translations)
    yield put({ type: actions.SESSION_SET_TRANSLATION_LOADING, payload: true })
    const { sessionId, translations, langCode, langName } = payload

    if (sessionTranslations.find(ele => ele.languageCode.toLowerCase() === langCode)) { // Update
      yield setSessionTranslation(sessionId, langCode, translations)
    } else { // Add
      yield addSessionTranslation(sessionId, langCode, translations)
      yield put({
        type: actions.SESSION_ADD_TRANSLATION,
        payload: { languageCode: langCode, languageName: langName }})
    }

    // Endpoint is pretty instant to add some delay so users know it did something
    yield delay(1000)
    yield put({
      type: actions.SESSION_SET_TRANSLATION_SUCCESS,
      payload: {
        langCode,
        translations
      }
    })
    yield put({ type: actions.SESSION_SET_TRANSLATION_LOADING, payload: false })
  } catch(error) {
    yield put({ type: actions.SESSION_SET_TRANSLATION_FAIL, payload: error })
  }
}

export const counselingSessionSagas = [
  takeEvery(actions.COUNSELING_SESSION_NAVIGATION, counselingSessionNavigationSaga),
  takeEvery(actions.GET_DECISION_RESULT, getDecisionResultSaga),
  takeEvery(actions.SAVE_SESSION_SEND_EMAIL, saveSessionSendEmailSaga),
  takeEvery(actions.SEND_EMAIL_REQUEST, sendReportEmailSaga),
  takeEvery(actions.SESSION_RECALL_REQUESTED, recallCounselingSessionSaga),
  takeEvery(actions.SESSION_REPORT_PDF_GENERATION_REQUESTED, generatePdfReportSaga),
  takeEvery(actions.SESSION_EXIT_REQUESTED, sessionExit),
  takeLatest(actions.UPDATE_USER_COMMENTS_REQUESTED, updateUserCommentsSaga),
  takeLatest(actions.DISMISS_FACTOR_ACTION_MODAL_REQUESTED, dismissFactorActionModal),
  takeLatest(actions.REQUEST_SESSION_TRANSLATIONS, getSessionTranslationsSaga),
  takeLatest(actions.SESSION_SET_TRANSLATION_INIT, setSessionTranslationsSaga)
]



// WEBPACK FOOTER //
// ./src/sagas/counseling-session.js